/**
 * Formats a delivered quantity value for display by rounding to 2 decimal places.
 * Removes unnecessary trailing zeros and decimal point for whole numbers.
 *
 * @param quantity - The delivered quantity value to format
 * @returns The formatted quantity as a string, or "-" for null/undefined values
 *
 * @example
 * formatDeliveredQuantity(123.456) // "123.46"
 * formatDeliveredQuantity(100) // "100"
 * formatDeliveredQuantity(97.00) // "97"
 * formatDeliveredQuantity(97.50) // "97.5"
 * formatDeliveredQuantity(null) // "-"
 * formatDeliveredQuantity(undefined) // "-"
 */
export function formatDeliveredQuantity(
  quantity: number | null | undefined
): string {
  if (quantity === null || quantity === undefined) {
    return "-";
  }

  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(quantity * 100) / 100;
  return rounded.toString();
}

/**
 * Rounds a delivered quantity value to 2 decimal places for display purposes.
 * Unlike formatDeliveredQuantity, this returns a number instead of a string.
 *
 * @param quantity - The delivered quantity value to round
 * @returns The rounded quantity as a number, or null/undefined for null/undefined values
 *
 * @example
 * roundDeliveredQuantity(123.456) // 123.46
 * roundDeliveredQuantity(100) // 100
 * roundDeliveredQuantity(null) // null
 * roundDeliveredQuantity(undefined) // undefined
 */
export function roundDeliveredQuantity(
  quantity: number | null | undefined
): number | null | undefined {
  if (quantity === null || quantity === undefined) {
    return quantity;
  }

  return Math.round(quantity * 100) / 100;
}
